import React, { createContext, useContext, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LanguageContext = createContext(null);

// Language is derived from the URL: "/en/..." => English, otherwise Spanish.
export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isEn = location.pathname === "/en" || location.pathname.startsWith("/en/");
  const lang = isEn ? "en" : "es";

  // Spanish (canonical) path, without the /en prefix.
  const basePath = isEn ? location.pathname.replace(/^\/en/, "") || "/" : location.pathname;

  // Localize a Spanish path to the current language.
  const lp = useCallback(
    (path) => {
      const p = path || "/";
      if (lang === "en") return p === "/" ? "/en" : "/en" + p;
      return p;
    },
    [lang]
  );

  const toggle = useCallback(() => {
    const target =
      lang === "en" ? basePath : basePath === "/" ? "/en" : "/en" + basePath;
    navigate(target + location.hash);
  }, [lang, basePath, location.hash, navigate]);

  const setLang = useCallback(
    (l) => {
      if (l !== lang) toggle();
    },
    [lang, toggle]
  );

  const t = useCallback(
    (value) => {
      if (value == null) return "";
      if (typeof value === "string") return value;
      return value[lang] ?? value.es ?? "";
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t, lp, basePath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
